import type { ExpectedTrailer } from './api.js';

/**
 * Mise en forme du prévisionnel de disponibilité.
 *
 * Extrait du JSX pour être testable. La règle qui compte est celle du LIBELLÉ DE
 * SOURCE : un écran qui affiche une heure sans dire d'où elle vient laisse
 * croire à un constat, et un transporteur se déplace pour rien. Le contrat porte
 * le champ `source` précisément pour ça ; l'écran doit le rendre lisible plutôt
 * que le recopier tel quel.
 */

export function libelleSource(source: ExpectedTrailer['source']): string {
  switch (source) {
    case 'constate':
      return 'sortie';
    case 'cadence_mesuree':
      return 'estimée — cadence du jour';
    case 'cadence_reference':
      return 'estimée — cadence de référence';
  }
}

/** `true` quand l'heure est une PRÉVISION et non un constat. */
export function estEstimation(source: ExpectedTrailer['source']): boolean {
  return source !== 'constate';
}

/** Heure locale, à la minute. La seconde n'apporte rien à un transporteur. */
export function heure(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export interface ResumePrevisionnel {
  total: number;
  sorties: number;
  attendues: number;
  enRetard: number;
  /** `null` tant qu'aucune remorque n'est sortie : il n'y a rien à annoncer. */
  prochaine: ExpectedTrailer | null;
}

/**
 * Ce qu'un exploitant lit en premier, avant même le tableau.
 *
 * `prochaine` est la première remorque encore attendue dans l'ordre de sortie —
 * pas la première en retard. Une remorque en retard est un problème à traiter,
 * pas une prévision à annoncer.
 */
export function resumer(lignes: readonly ExpectedTrailer[]): ResumePrevisionnel {
  const sorties = lignes.filter((l) => !estEstimation(l.source));
  const attendues = lignes.filter((l) => estEstimation(l.source));
  return {
    total: lignes.length,
    sorties: sorties.length,
    attendues: attendues.length,
    enRetard: lignes.filter((l) => l.is_overdue).length,
    prochaine: attendues.find((l) => !l.is_overdue) ?? null,
  };
}
